import { action, observable } from 'mobx';
import { HttpClient, IConfig, setMap } from 'Mri.Web.Common';
import { UserListViewTypes } from './UserListViewSelector';

export class UsersStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    @action
    public async loadUsersAsync(config?: IConfig) {
        const users = await this.httpClient.get<Mri.Web.Portal.Controllers.Account.UserModel[]>(
            '/api/v1/users',
            config
        );

        setMap(this.users, users, u => u.id);
    }

    @action
    public async loadUserAsync(id: string) {
        const user = await this.httpClient.get<Mri.Web.Portal.Controllers.Account.UserModel>(`/api/v1/users/${id}`);

        if (user) this.users.set(user.id, user);
    }

    @action
    public async deleteAsync(user: Mri.Web.Portal.Controllers.Account.UserModel) {
        await this.httpClient.delete(`/api/v1/users/${user.id}`);

        this.users.delete(user.id);
    }

    @action
    public async lockAsync(user: Mri.Web.Portal.Controllers.Account.UserModel, lock: boolean) {
        const newUser = await this.httpClient.post<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/users/${user.id}/lock`,
            {
                lock,
            }
        );

        if (newUser) this.users.set(newUser.id, newUser);
    }

    @action
    public async subscribeUserToAlerts(user: Mri.Web.Portal.Controllers.Account.UserModel, subscribed: boolean) {
        const newUser = await this.httpClient.post<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/users/${user.id}/subscribeToAlerts`,
            {
                subscribed,
            }
        );

        if (newUser) this.users.set(newUser.id, newUser);
    }

    @action
    public async addAccountAsync(
        user: Mri.Web.Portal.Controllers.Account.UserModel,
        model: Mri.Web.Portal.Controllers.Users.AddAccountModel
    ) {
        const newUser = await this.httpClient.post<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/users/${user.id}/accounts`,
            model
        );

        if (newUser) this.users.set(newUser.id, newUser);
    }

    @action
    public async removeAccountAsync(user: Mri.Web.Portal.Controllers.Account.UserModel, account: string) {
        const newUser = await this.httpClient.delete<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/users/${user.id}/accounts/${account}`
        );

        if (newUser) this.users.set(newUser.id, newUser);
    }

    @action
    public async addRoleAsync(
        user: Mri.Web.Portal.Controllers.Account.UserModel,
        model: Mri.Web.Portal.Controllers.Users.AddRoleModel
    ) {
        const newUser = await this.httpClient.post<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/users/${user.id}/roles`,
            model
        );

        if (newUser) this.users.set(newUser.id, newUser);
    }

    @action
    public async removeRoleAsync(user: Mri.Web.Portal.Controllers.Account.UserModel, role: string) {
        const newUser = await this.httpClient.delete<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/users/${user.id}/roles/${role}`
        );

        if (newUser) this.users.set(newUser.id, newUser);
    }

    public async sendResetPasswordAsync(user: Mri.Web.Portal.Controllers.Account.UserModel) {
        await this.httpClient.post(`/api/v1/users/${user.id}/reset-password`);
    }

    @observable
    public users = new Map<string, Mri.Web.Portal.Controllers.Account.UserModel>();
    @observable
    public productionUsers = new Map<string, Mri.Web.Portal.Controllers.Account.UserModel>();
    @observable
    public viewType: UserListViewTypes = UserListViewTypes.UserView;
    private readonly httpClient: HttpClient;
}
